import { ReactNode } from "react";
import { Space, Typography } from "antd";
import styled from "@emotion/styled";

const { Text } = Typography;

export interface ChoiceCardOption<T extends string | number> {
  value: T;
  label: string;
  description?: string;
  icon: ReactNode;
}

interface ChoiceCardGroupProps<T extends string | number> {
  value?: T;
  onChange?: (value: T) => void;
  /** Extra callback after selection (Form.Item still owns onChange). */
  onSelect?: (value: T) => void;
  options: ChoiceCardOption<T>[];
  disabled?: boolean;
}

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  width: 100%;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const CardButton = styled.button<{ $selected: boolean; $disabled?: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-height: 56px;
  padding: 12px 14px;
  margin: 0;
  text-align: left;
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  border-radius: 10px;
  border: 2px solid
    ${({ $selected }) => ($selected ? "#1677ff" : "#d9d9d9")};
  background: ${({ $selected }) => ($selected ? "#e6f4ff" : "#fff")};
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
  transition: border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
  box-shadow: ${({ $selected }) =>
    $selected ? "0 0 0 1px rgba(22, 119, 255, 0.2)" : "none"};

  &:hover:not(:disabled) {
    border-color: #1677ff;
  }

  &:focus-visible {
    outline: 2px solid #1677ff;
    outline-offset: 2px;
  }
`;

const IconWrap = styled.div<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  font-size: 18px;
  color: ${({ $selected }) => ($selected ? "#1677ff" : "#595959")};
  background: ${({ $selected }) => ($selected ? "#fff" : "#f5f5f5")};
`;

export function ChoiceCardGroup<T extends string | number>({
  value,
  onChange,
  onSelect,
  options,
  disabled,
}: ChoiceCardGroupProps<T>) {
  return (
    <Grid role="radiogroup">
      {options.map((option) => {
        const selected = value === option.value;
        return (
          <CardButton
            key={String(option.value)}
            type="button"
            role="radio"
            aria-checked={selected}
            disabled={disabled}
            $selected={selected}
            $disabled={disabled}
            onClick={() => {
              if (disabled) return;
              onChange?.(option.value);
              onSelect?.(option.value);
            }}
          >
            <IconWrap $selected={selected}>{option.icon}</IconWrap>
            <Space direction="vertical" size={0} style={{ minWidth: 0 }}>
              <Text strong style={{ fontSize: 14, lineHeight: 1.3 }}>
                {option.label}
              </Text>
              {option.description && (
                <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.3 }}>
                  {option.description}
                </Text>
              )}
            </Space>
          </CardButton>
        );
      })}
    </Grid>
  );
}
